import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import {
  api,
  getSessionCookieToken,
  type ForecastConfig,
  type ForecastParamsSchema,
  type ForecastSource,
  type ForecastSourceType,
  type ForecastStatus,
} from '../api/client'
import LoadingOverlay from '../components/LoadingOverlay'

const EMPTY: ForecastConfig = { sources: [] }

type Tuning = 'backfill_days' | 'label_min_impact' | 'label_mad_k'

const TUNING: { key: Tuning; label: string; hint: string }[] = [
  { key: 'backfill_days', label: 'Backfill days', hint: 'How far back to load history from sources that keep it.' },
  {
    key: 'label_min_impact',
    label: 'Incident floor',
    hint: 'Minimum affected users (or error lines) per 5 minutes for a burst to count as an incident.',
  },
  {
    key: 'label_mad_k',
    label: 'Burst sensitivity (k)',
    hint: 'A burst must exceed the trailing-day median by k × MAD. Lower finds more incidents.',
  },
]

function fmtTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—'
}

function fmtNum(v: unknown): string {
  return typeof v === 'number' ? (Number.isInteger(v) ? String(v) : v.toFixed(3)) : '—'
}

/** One input per param, driven by the adapter's JSON schema — new source types need no UI change. */
function SourceFields({
  schema,
  source,
  onChange,
}: {
  schema: ForecastParamsSchema
  source: ForecastSource
  onChange: (next: ForecastSource) => void
}) {
  const required = new Set(schema.required ?? [])
  const props = Object.entries(schema.properties ?? {})
  if (props.length === 0) {
    return <p className="notif-settings-hint">No settings — uses this workspace&apos;s existing integration.</p>
  }
  return (
    <>
      {props.map(([key, prop]) => {
        const id = `src-${key}-${source.type}`
        const value = source[key]
        const isList = prop.type === 'array'
        const isNumber = prop.type === 'integer' || prop.type === 'number'
        return (
          <div className="cred-field" key={key}>
            <label className="cred-label" htmlFor={id}>
              {prop.title ?? key}
              {required.has(key) ? ' *' : ''}
            </label>
            {prop.type === 'boolean' ? (
              <input
                id={id}
                type="checkbox"
                checked={Boolean(value ?? prop.default)}
                onChange={e => onChange({ ...source, [key]: e.target.checked })}
              />
            ) : (
              <input
                id={id}
                className="cred-input"
                type={isNumber ? 'number' : 'text'}
                value={isList ? ((value as string[] | undefined) ?? []).join(', ') : String(value ?? '')}
                placeholder={prop.default !== undefined ? String(prop.default) : ''}
                onChange={e => {
                  const raw = e.target.value
                  const next: ForecastSource = { ...source }
                  if (isList) next[key] = raw.split(',').map(s => s.trim()).filter(Boolean)
                  else if (raw === '') delete next[key] // fall back to the default
                  else next[key] = isNumber ? Number(raw) : raw
                  onChange(next)
                }}
              />
            )}
            {prop.description && <p className="notif-settings-hint">{prop.description}</p>}
          </div>
        )
      })}
    </>
  )
}

function StatusPanel({ status }: { status: ForecastStatus | null }) {
  if (!status) return null
  const active = status.runs.find(r => r.active)
  return (
    <section className="notif-settings-panel notif-settings-panel--wide">
      <div className="notif-settings-header">
        <h2 className="integration-section-title" style={{ margin: 0 }}>Status</h2>
      </div>

      <fieldset className="notif-settings-group">
        <legend className="notif-settings-legend">Current forecast</legend>
        {status.probability === null ? (
          <p className="notif-settings-hint">
            No active model yet. A model is activated once enough incidents are collected to evaluate it and it
            beats the baseline.
          </p>
        ) : (
          <p style={{ margin: 0 }}>
            <strong style={{ fontSize: '20px' }}>{(status.probability * 100).toFixed(1)}%</strong> chance an
            incident starts in the next 30 minutes{' '}
            <span className={`badge ${status.flagged ? 'badge-error' : 'badge-success'}`}>
              {status.flagged ? 'above threshold' : 'normal'}
            </span>
            <span className="notif-settings-hint"> · scored {fmtTime(status.scored_at)}</span>
          </p>
        )}
        {active && <p className="notif-settings-hint">Active model trained {fmtTime(active.created_at)}.</p>}
      </fieldset>

      <fieldset className="notif-settings-group">
        <legend className="notif-settings-legend">Data collected</legend>
        {status.sources.length === 0 ? (
          <p className="notif-settings-hint">Nothing collected yet — the collector runs every 5 minutes.</p>
        ) : (
          <table className="inv-table">
            <thead>
              <tr><th>Source</th><th>Latest bucket</th><th>Points (24h)</th></tr>
            </thead>
            <tbody>
              {status.sources.map(s => (
                <tr key={s.source}>
                  <td>{s.source}</td>
                  <td>{fmtTime(s.last_bucket)}</td>
                  <td>{s.points_24h.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </fieldset>

      <fieldset className="notif-settings-group">
        <legend className="notif-settings-legend">Training runs</legend>
        {status.runs.length === 0 ? (
          <p className="notif-settings-hint">No training runs yet — training runs daily.</p>
        ) : (
          <table className="inv-table">
            <thead>
              <tr>
                <th>Trained</th><th>Incidents</th><th>GRU AP</th><th>Baseline AP</th><th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {status.runs.map(r => (
                <tr key={r.id}>
                  <td>{fmtTime(r.created_at)}</td>
                  <td>{fmtNum(r.metrics.onsets_total)}</td>
                  <td>{fmtNum(r.metrics.gru_ap)}</td>
                  <td>{fmtNum(r.metrics.baseline_ap)}</td>
                  <td>
                    <span className={`badge ${r.active ? 'badge-success' : 'badge-info'}`}>
                      {r.active ? 'active' : 'inactive'}
                    </span>{' '}
                    {String(r.metrics.skipped ?? r.metrics.decision ?? '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </fieldset>
    </section>
  )
}

export default function ForecastSettings() {
  const { getToken } = useAuth()
  const [config, setConfig] = useState<ForecastConfig>(EMPTY)
  const [saved, setSaved] = useState(false) // a config exists in the vault
  const [defaults, setDefaults] = useState<Omit<ForecastConfig, 'sources'>>({})
  const [types, setTypes] = useState<ForecastSourceType[]>([])
  const [status, setStatus] = useState<ForecastStatus | null>(null)
  const [newType, setNewType] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  async function authToken(): Promise<string> {
    const t = getSessionCookieToken() ?? (await getToken())
    if (!t) throw new Error('Not authenticated')
    return t
  }

  useEffect(() => {
    ;(async () => {
      try {
        const t = await authToken()
        const [cfg, st] = await Promise.all([api.getForecastConfig(t), api.getForecastStatus(t)])
        setConfig(cfg.config ?? EMPTY)
        setSaved(cfg.config !== null)
        setDefaults(cfg.defaults)
        setTypes(cfg.source_types)
        setNewType(cfg.source_types[0]?.type ?? '')
        setStatus(st)
      } catch (err) {
        setMessage({ ok: false, text: (err as Error).message })
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function edit(next: ForecastConfig) {
    setConfig(next)
    setMessage(null)
  }

  function setSource(i: number, next: ForecastSource) {
    edit({ ...config, sources: config.sources.map((s, j) => (j === i ? next : s)) })
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      const res = await api.saveForecastConfig(await authToken(), config)
      setConfig(res.config ?? EMPTY)
      setSaved(true)
      setMessage({ ok: true, text: 'Saved. The collector picks it up within 5 minutes.' })
    } catch (err) {
      setMessage({ ok: false, text: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  async function handleDisable() {
    if (!window.confirm('Stop forecasting for this workspace? Collected data and trained models are kept.')) return
    setSaving(true)
    try {
      await api.deleteForecastConfig(await authToken())
      setConfig(EMPTY)
      setSaved(false)
      setMessage({ ok: true, text: 'Forecasting disabled.' })
    } catch (err) {
      setMessage({ ok: false, text: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingOverlay minHeight="240px" />

  const typeOf = (t: string) => types.find(x => x.type === t)

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="text-display-md" style={{ margin: 0 }}>Incident Forecast</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
            Learn what your logs, errors and database look like before an incident, and predict the next one.
          </p>
        </div>
      </div>

      <StatusPanel status={status} />

      <section className="notif-settings-panel notif-settings-panel--wide">
        <div className="notif-settings-header">
          <h2 className="integration-section-title" style={{ margin: 0 }}>Data sources</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
            Add the connections this workspace has. Each one is turned into per-service counts every 5 minutes.
          </p>
        </div>

        <form onSubmit={handleSave} className="notif-settings-form">
          {config.sources.map((source, i) => {
            const type = typeOf(source.type)
            return (
              <fieldset className="notif-settings-group" key={i}>
                <legend className="notif-settings-legend">
                  {source.name || source.type}{' '}
                  <span className="badge badge-info">{type?.history ? 'with history' : 'live only'}</span>
                </legend>
                {type?.description && <p className="notif-settings-hint">{type.description}</p>}
                <div className="cred-field">
                  <label className="cred-label" htmlFor={`src-name-${i}`}>Name</label>
                  <input
                    id={`src-name-${i}`}
                    className="cred-input"
                    value={source.name ?? ''}
                    placeholder={source.type}
                    onChange={e => {
                      const next: ForecastSource = { ...source, name: e.target.value }
                      if (!e.target.value) delete next.name
                      setSource(i, next)
                    }}
                  />
                  <p className="notif-settings-hint">Only needed to tell two sources of the same type apart.</p>
                </div>
                <div className="cred-field">
                  <label className="cred-label" htmlFor={`src-via-${i}`}>Fetched by</label>
                  <select
                    id={`src-via-${i}`}
                    className="cred-input"
                    value={source.via ?? 'healops'}
                    onChange={e => {
                      const next: ForecastSource = { ...source, via: e.target.value as 'healops' | 'agent' }
                      if (next.via === 'healops') delete next.via
                      setSource(i, next)
                    }}
                  >
                    <option value="healops">HealOps (publicly reachable)</option>
                    <option value="agent">Forecast agent (inside your network / VPN)</option>
                  </select>
                </div>
                {type ? (
                  <SourceFields schema={type.params_schema} source={source} onChange={next => setSource(i, next)} />
                ) : (
                  <p className="cred-error">Unknown source type “{source.type}”.</p>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn-secondary-outline"
                    onClick={() => edit({ ...config, sources: config.sources.filter((_, j) => j !== i) })}
                  >
                    Remove
                  </button>
                </div>
              </fieldset>
            )
          })}

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select className="cred-input" value={newType} onChange={e => setNewType(e.target.value)}>
              {types.map(t => (
                <option key={t.type} value={t.type}>{t.type}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn-secondary-outline"
              disabled={!newType}
              onClick={() => edit({ ...config, sources: [...config.sources, { type: newType }] })}
            >
              Add source
            </button>
          </div>

          <fieldset className="notif-settings-group">
            <legend className="notif-settings-legend">Tuning</legend>
            {TUNING.map(({ key, label, hint }) => (
              <div className="cred-field" key={key}>
                <label className="cred-label" htmlFor={`tune-${key}`}>{label}</label>
                <input
                  id={`tune-${key}`}
                  className="cred-input"
                  type="number"
                  step="any"
                  value={config[key] ?? ''}
                  placeholder={String(defaults[key] ?? '')}
                  onChange={e => {
                    const next = { ...config }
                    if (e.target.value === '') delete next[key]
                    else next[key] = Number(e.target.value)
                    edit(next)
                  }}
                />
                <p className="notif-settings-hint">{hint}</p>
              </div>
            ))}
          </fieldset>

          {message && (
            <p className={message.ok ? 'cred-success' : 'cred-error'} style={{ margin: 0 }}>{message.text}</p>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            {saved && (
              <button type="button" className="btn-secondary-outline" disabled={saving} onClick={handleDisable}>
                Disable forecasting
              </button>
            )}
            <button type="submit" className="cred-save-btn" disabled={saving || config.sources.length === 0}>
              {saving ? 'Saving…' : 'Save configuration'}
            </button>
          </div>
        </form>
      </section>

      {config.sources.some(src => src.via === 'agent') && <AgentPanel authToken={authToken} />}
    </div>
  )
}

/** Issue the agent token and show how to run the agent next to the private sources. */
function AgentPanel({ authToken }: { authToken: () => Promise<string> }) {
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const apiUrl = import.meta.env.VITE_API_URL || window.location.origin

  async function issue() {
    if (!window.confirm('Issue a new agent token? Any running agent with the old token stops working.')) return
    setBusy(true)
    setError(null)
    try {
      setToken((await api.issueForecastAgentToken(await authToken())).token)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const command = `HEALOPS_URL=${apiUrl} \\\n  HEALOPS_FORECAST_TOKEN=${token ?? '<token>'} \\\n  python -m app.forecast.agent --loop 300`

  return (
    <section className="notif-settings-panel notif-settings-panel--wide">
      <div className="notif-settings-header">
        <h2 className="integration-section-title" style={{ margin: 0 }}>Forecast agent</h2>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
          Sources fetched by the agent are read from inside your network — run it in your cluster or on a host
          on your VPN. It sends only per-service counts to HealOps.
        </p>
      </div>
      <fieldset className="notif-settings-group">
        <legend className="notif-settings-legend">Run it</legend>
        <pre className="cred-input" style={{ whiteSpace: 'pre-wrap', fontSize: '12px', margin: 0 }}>{command}</pre>
        <p className="notif-settings-hint">
          In Kubernetes, run the HealOps image as a CronJob every 5 minutes with the same two variables (store the
          token in a Secret). Sources such as <code>postgres</code> read their credentials from the agent&apos;s
          environment.
        </p>
        {token && (
          <p className="cred-success" style={{ margin: 0 }}>
            Copy the token now — it is not shown again.
          </p>
        )}
        {error && <p className="cred-error" style={{ margin: 0 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="cred-save-btn" disabled={busy} onClick={issue}>
            {busy ? 'Issuing…' : 'Issue agent token'}
          </button>
        </div>
      </fieldset>
    </section>
  )
}
